import "leaflet-plugin-trackplayback";
import L from "leaflet";

const getAssetIcon = (typeMobileId) => {
  switch (typeMobileId) {
    case 1:
      return '/assets/img/Minivan.png';
    case 2:
      return '/assets/img/Truck.png';
    case 3:
      return '/assets/img/Man.png';
    case 4:
      return '/assets/img/Tracktor.png';
    case 5:
      return '/assets/img/Sport.png';
    case 6:
      return '/assets/img/Custer.png';
    case 7:
      return '/assets/img/Bus.png';
    case 8:
      return '/assets/img/Station.png';
    case 9:
      return '/assets/img/Van.png';
    case 10:
      return '/assets/img/Sedan.png';
    case 11:
      return '/assets/img/Icon4x4.png';
    case 12:
      return '/assets/img/Moto.png';
    default:
      return '/assets/img/Bus.png';
  }
}

// Each PNG has a different amount of whitespace around the vehicle graphic.
// anchorFractionX/Y are the visual center of the vehicle relative to the
// full image dimensions so iconAnchor and transform-origin stay in sync.
const getIconConfig = (typeMobileId) => {
  switch (typeMobileId) {
    // Vehicle graphic sits in the upper-left ~10% of the canvas
    case 3:  // Man
    case 4:  // Tracktor
    case 7:  // Bus
    case 9:  // Van
      return { anchorFractionX: 0.10, anchorFractionY: 0.11 };
    // Vehicle fills the full width, occupies the upper ~65-75% of height
    case 1:  // Minivan
    case 2:  // Truck
    case 8:  // Station
    case 10: // Sedan
    case 11: // Icon4x4
    case 12: // Moto
      return { anchorFractionX: 0.50, anchorFractionY: 0.34 };
    // Vehicle is centered in the canvas (Sport, Custer, and unknown types)
    default:
      return { anchorFractionX: 0.50, anchorFractionY: 0.50 };
  }
}

const getDivContainer = () => {
  const container = L.DomUtil.create(
    "div",
    "leaflet-bar leaflet-control leaflet-control-custom"
  );
  container.style.backgroundColor = "white";
  container.style.width = "230px";
  container.style.height = "42px";
  container.style.border = "1px solid black";
  container.style.marginTop = "100px";
  container.style.position = "relative";
  container.style.display = "block";

  return container;
};

const getInnerContainer = () => {
  const container = L.DomUtil.create("div");

  container.style.margin = "0";
  container.style.position = "absolute";
  container.style.top = "50%";
  container.style.transform = "translateY(-50%)";

  return container;
};

const actionIconStyle = {
  width: "32px",
  height: "32px",
  backgroundSize: "32px 32px",
  border: "0",
  backgroundColor: "transparent",
  margin: "5px 0 0 10px",
};

const getPlayButton = (t) => {
  const playButton = L.DomUtil.create("button");

  playButton.style["backgroundImage"] = "url(/assets/img/playIcon.png)";
  playButton.title = t("start");

  //style object is readonly so we can't assign a new object with the merged styles
  Object.entries(actionIconStyle).forEach(([key, value]) => {
    playButton.style[key] = value;
  });

  return playButton;
};

const getPauseButton = (t) => {
  const pauseButton = L.DomUtil.create("button");

  pauseButton.style.backgroundImage = "url(/assets/img/pauseIcon.png)";
  pauseButton.style.display = "none";
  pauseButton.title = t("pause");
  Object.entries(actionIconStyle).forEach(([key, value]) => {
    pauseButton.style[key] = value;
  });

  return pauseButton;
};

const getRewindButton = (t) => {
  const rewindButton = L.DomUtil.create("button");

  rewindButton.style.backgroundImage = "url(/assets/img/replayIcon.png)";
  rewindButton.title = t("rewind");
  Object.entries(actionIconStyle).forEach(([key, value]) => {
    rewindButton.style[key] = value;
  });

  return rewindButton;
};

const getSlowButton = (t) => {
  const slowButton = L.DomUtil.create("button");

  slowButton.style.backgroundImage = "url(/assets/img/slowIcon.png)";
  slowButton.title = t("slow");
  Object.entries(actionIconStyle).forEach(([key, value]) => {
    slowButton.style[key] = value;
  });

  return slowButton;
};

const getFastButton = (t) => {
  const fastButton = L.DomUtil.create("button");

  fastButton.style.backgroundImage = "url(/assets/img/fastIcon.png)";
  fastButton.title = t("faster");
  Object.entries(actionIconStyle).forEach(([key, value]) => {
    fastButton.style[key] = value;
  });

  return fastButton;
};

const getClearButton = (t) => {
  const clearButton = L.DomUtil.create("button");

  clearButton.style.backgroundImage = "url(/assets/img/clearIcon.png)";
  clearButton.title = t("clear");
  Object.entries(actionIconStyle).forEach(([key, value]) => {
    clearButton.style[key] = value;
  });

  return clearButton;
};

const normalizeTrackTimestamps = (track) => {
  if (!track || track.length < 2) return track;
  return track.map((point, index) => ({ ...point, time: index }));
};

const getControlInstance = (
  mapInstance,
  track,
  playbackTickHandler,
  closePanel,
  updateMarkerJourney,
  onReady = () => { },
  onRemove = () => { },
  t,
  typeMobileId
) => {
  const normalizedTrack = normalizeTrackTimestamps(track);
  // Target ~45s of playback. Formula: speed = 1 + log2(points / targetSeconds)
  // clamped to [1, 12] to stay within library limits.
  const TARGET_PLAYBACK_SECONDS = 60;
  const autoSpeed = Math.max(1, Math.min(12, Math.round(1 + Math.log2(normalizedTrack.length / TARGET_PLAYBACK_SECONDS))));
  const trackOptions = {
    clockOptions: {
      speed: autoSpeed,
    },
    trackPointOptions: {
      useCanvas: false,
    },
    // Hide the built-in canvas icon — we use a native L.Marker instead so it
    // shares the same coordinate system as the route polyline.
    targetOptions: {
      useImg: false,
      width: 1,
      height: 1,
      color: "transparent",
      fillColor: "transparent",
    },
  };

  const ICON_SIZE = 125;
  // const { anchorFractionX, anchorFractionY } = getIconConfig(typeMobileId);
  const { anchorFractionX, anchorFractionY } = { anchorFractionX: 0.10, anchorFractionY: 0.10 };
  const anchorX = Math.round(ICON_SIZE * anchorFractionX);
  const anchorY = Math.round(ICON_SIZE * anchorFractionY);
  const transformOrigin = `${Math.round(anchorFractionX * 100)}% ${Math.round(anchorFractionY * 100)}%`;
  const vehicleIcon = L.divIcon({
    className: "",
    html: `<img src="${getAssetIcon(typeMobileId)}" style="width:${ICON_SIZE}px;height:${ICON_SIZE}px;display:block;transform-origin:${transformOrigin};" />`,
    iconSize: [ICON_SIZE, ICON_SIZE],
    iconAnchor: [anchorX, anchorY],
  });

  const vehicleMarker = L.marker(
    [normalizedTrack[0].lat, normalizedTrack[0].lng],
    { icon: vehicleIcon, zIndexOffset: 1000 }
  ).addTo(mapInstance);

  if (normalizedTrack && normalizedTrack.length > 0) {
    const trackplayback = L.trackplayback(normalizedTrack, mapInstance, trackOptions);

    trackplayback.on("tick", (e) => {
      const { time, target } = e;

      if (target && target.tracks && target.tracks.length > 0) {
        const currentPt = target.tracks[0]._getCalculateTrackPointByTime(time);
        if (currentPt) {
          // Move the native marker — same coordinate system as the polyline
          vehicleMarker.setLatLng([currentPt.lat, currentPt.lng]);
          // Apply heading rotation to the image inside the DivIcon
          const el = vehicleMarker.getElement();
          if (el) {
            const img = el.querySelector("img");
            if (img) img.style.transform = `rotate(${currentPt.dir || 0}deg)`;
          }
          mapInstance.panTo([currentPt.lat, currentPt.lng], { animate: false });
        }
      }

      const matchPoint =
        target &&
        target.tracks &&
        target.tracks[0]._trackPoints.filter(
          (p) => parseInt(p.time, 10) === parseInt(time, 10)
        );

      if (matchPoint && matchPoint.length > 0) {
        playbackTickHandler(matchPoint[0].info[0].value);
      }
    });

    const playControl = L.Control.extend({
      options: {
        position: "topright",
      },
      onAdd: () => {
        const divContainer = getDivContainer();
        const innerContainer = getInnerContainer();
        const playButton = getPlayButton(t);
        const pauseButton = getPauseButton(t);
        const rewindButton = getRewindButton(t);
        const slowButton = getSlowButton(t);
        const fastButton = getFastButton(t);
        const clearButton = getClearButton(t);

        playButton.onclick = () => {
          trackplayback.start();
          playButton.style.display = "none";
          pauseButton.style.display = "inline-block";
        };

        pauseButton.onclick = () => {
          trackplayback.stop();
          playButton.style.display = "inline-block";
          pauseButton.style.display = "none";
        };

        rewindButton.onclick = () => trackplayback.rePlaying();
        slowButton.onclick = () => trackplayback.slowSpeed();
        fastButton.onclick = () => trackplayback.quickSpeed();
        clearButton.onclick = () => {
          if (trackplayback) {
            trackplayback.stop();
            trackplayback.dispose();
            vehicleMarker.remove();
            updateMarkerJourney && updateMarkerJourney({});
            closePanel && closePanel();
          }
          divContainer.style.display = "none";
        };

        divContainer.appendChild(innerContainer);
        innerContainer.appendChild(playButton);
        innerContainer.appendChild(pauseButton);
        innerContainer.appendChild(rewindButton);
        innerContainer.appendChild(slowButton);
        innerContainer.appendChild(fastButton);
        innerContainer.appendChild(clearButton);

        onReady();
        return divContainer;
      },
      onRemove: () => {
        try {
          if (trackplayback) {
            trackplayback.stop();
            trackplayback.dispose();
          }
          vehicleMarker.remove();
        } catch (err) {
          //
        }
      },
    });

    const pc = new playControl();
    return pc;
  }
  return null;
};


export default getControlInstance;
